import { startTransition, useCallback, useEffect, useRef, useState } from "react";
import {
    AUDIO_LEVEL_UPDATE_INTERVAL_MS,
    AUDIO_LEVEL_WARMUP_MS,
    AUDIO_STATUS_GRACE_MS,
} from "./constants";
import { clampAudioLevel } from "./utils";

export default function useAudioLevelMonitor() {
    const [audioLevel, setAudioLevel] = useState(0);
    const [isInRecordingGracePeriod, setIsInRecordingGracePeriod] = useState(false);

    const audioMonitoringTimeoutRef = useRef(null);
    const smoothedAudioLevelRef = useRef(0);
    const audioMonitoringStartedAtRef = useRef(0);
    const audioStatusGraceTimeoutRef = useRef(null);
    const audioLevelSamplesRef = useRef([]);

    const clearAudioStatusGraceTimeout = useCallback(() => {
        if (audioStatusGraceTimeoutRef.current) {
            clearTimeout(audioStatusGraceTimeoutRef.current);
            audioStatusGraceTimeoutRef.current = null;
        }
    }, []);

    const restartAudioStatusGracePeriod = useCallback(() => {
        clearAudioStatusGraceTimeout();
        setIsInRecordingGracePeriod(true);
        audioStatusGraceTimeoutRef.current = window.setTimeout(() => {
            audioStatusGraceTimeoutRef.current = null;
            setIsInRecordingGracePeriod(false);
        }, AUDIO_STATUS_GRACE_MS);
    }, [clearAudioStatusGraceTimeout]);

    const stopAudioLevelMonitoring = useCallback(() => {
        if (audioMonitoringTimeoutRef.current) {
            clearTimeout(audioMonitoringTimeoutRef.current);
            audioMonitoringTimeoutRef.current = null;
        }

        clearAudioStatusGraceTimeout();
        smoothedAudioLevelRef.current = 0;
        audioMonitoringStartedAtRef.current = 0;
        audioLevelSamplesRef.current = [];
        setIsInRecordingGracePeriod(false);
        setAudioLevel(0);
    }, [clearAudioStatusGraceTimeout]);

    function startAudioLevelMonitoring(analyser) {
        if (!analyser) {
            return;
        }

        // เริ่มใหม่ทุกครั้งที่เริ่ม/กลับมาอัด เพื่อไม่ให้ sample เก่าปน segment ใหม่
        stopAudioLevelMonitoring();

        const timeDomainSamples = new Uint8Array(analyser.fftSize);
        const frequencySamples = new Uint8Array(analyser.frequencyBinCount);
        audioMonitoringStartedAtRef.current = Date.now();
        restartAudioStatusGracePeriod();

        function tick() {
            analyser.getByteTimeDomainData(timeDomainSamples);
            analyser.getByteFrequencyData(frequencySamples);

            let timeDomainEnergy = 0;
            for (let index = 0; index < timeDomainSamples.length; index += 1) {
                const normalizedSample = (timeDomainSamples[index] - 128) / 128;
                timeDomainEnergy += normalizedSample * normalizedSample;
            }

            let frequencyTotal = 0;
            let frequencyPeak = 0;
            for (let index = 0; index < frequencySamples.length; index += 1) {
                const value = frequencySamples[index];
                frequencyTotal += value;
                if (value > frequencyPeak) {
                    frequencyPeak = value;
                }
            }

            const rms = Math.sqrt(timeDomainEnergy / timeDomainSamples.length);
            const averageFrequencyLevel = frequencySamples.length
                ? frequencyTotal / frequencySamples.length / 255
                : 0;
            const peakFrequencyLevel = frequencyPeak / 255;
            const rawLevel = Math.max(
                rms * 12,
                averageFrequencyLevel * 1.8,
                peakFrequencyLevel * 0.85
            );
            const boostedLevel = clampAudioLevel(Math.pow(rawLevel, 0.85));
            const isWarmingUp = (Date.now() - audioMonitoringStartedAtRef.current) < AUDIO_LEVEL_WARMUP_MS;
            const targetLevel = isWarmingUp ? 0 : boostedLevel;
            const previousLevel = smoothedAudioLevelRef.current;
            const smoothingFactor = targetLevel > previousLevel ? 0.45 : 0.18;
            const nextLevel = previousLevel + ((targetLevel - previousLevel) * smoothingFactor);

            smoothedAudioLevelRef.current = nextLevel;
            audioLevelSamplesRef.current.push({
                timestamp: Date.now(),
                level: nextLevel,
            });
            startTransition(() => {
                setAudioLevel(nextLevel);
            });

            audioMonitoringTimeoutRef.current = window.setTimeout(
                tick,
                AUDIO_LEVEL_UPDATE_INTERVAL_MS
            );
        }

        tick();
    }

    function consumeAudioLevelSampleBatchUntil(
        timestamp,
        {
            durationMs = 0,
            segmentStartedAt = timestamp,
        } = {}
    ) {
        const samples = audioLevelSamplesRef.current;

        if (!samples.length) {
            const fallbackLevel = smoothedAudioLevelRef.current;

            return {
                averageLevel: fallbackLevel,
                samples: durationMs > 0
                    ? [{ level: fallbackLevel, offsetMs: durationMs }]
                    : [],
            };
        }

        let total = 0;
        let count = 0;
        let consumedCount = 0;
        const consumedSamples = [];

        while (consumedCount < samples.length && samples[consumedCount].timestamp <= timestamp) {
            const sample = samples[consumedCount];
            total += sample.level;
            count += 1;
            consumedSamples.push(sample);
            consumedCount += 1;
        }

        if (consumedCount > 0) {
            audioLevelSamplesRef.current = samples.slice(consumedCount);
        }

        const averageLevel = count ? total / count : smoothedAudioLevelRef.current;
        // แปลง timestamp จริงให้เป็น offset ภายใน segment ปัจจุบัน
        const normalizedSamples = consumedSamples.length
            ? consumedSamples.map((sample) => ({
                level: sample.level,
                offsetMs: Math.max(
                    0,
                    Math.min(
                        durationMs || 0,
                        sample.timestamp - segmentStartedAt
                    )
                ),
            }))
            : durationMs > 0
                ? [{ level: averageLevel, offsetMs: durationMs }]
                : [];

        return {
            averageLevel,
            samples: normalizedSamples,
        };
    }

    function consumeAudioLevelSamplesUntil(timestamp) {
        return consumeAudioLevelSampleBatchUntil(timestamp).averageLevel;
    }

    useEffect(() => {
        return () => {
            stopAudioLevelMonitoring();
        };
    }, [stopAudioLevelMonitoring]);

    return {
        audioLevel,
        consumeAudioLevelSampleBatchUntil,
        consumeAudioLevelSamplesUntil,
        isInRecordingGracePeriod,
        startAudioLevelMonitoring,
        stopAudioLevelMonitoring,
    };
}
